import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Ship, Search, Filter, Plus, MapPin, Calendar, ChevronRight,
  Anchor, AlertTriangle, CheckCircle2, Clock, Eye, MoreHorizontal,
  ArrowUpRight, Download, Grid3X3, List, Globe2, Weight, CalendarDays,
  ClipboardCheck, X
} from 'lucide-react';
import { useAppStore } from '../../store/useAppStore';
import { formatDate, getScoreColor, getScoreBgColor } from '../../utils/helpers';
import { mockFleets } from '../../data/mockData';
import type { VesselType, VesselStatus } from '../../types';

export default function FleetPage() {
  const navigate = useNavigate();
  const { vessels, addVessel } = useAppStore();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [view, setView] = useState<'grid' | 'list'>('grid');
  const [selectedVessel, setSelectedVessel] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    imoNumber: '',
    type: 'Bulk Carrier' as VesselType,
    flag: '',
    deadweight: 0,
    grossTonnage: 0,
    yearBuilt: new Date().getFullYear(),
    classificationSociety: '',
    manager: '',
    superintendent: '',
    status: 'active' as VesselStatus,
    fleetId: 'fl-001',
    lastRightShipScore: undefined as number | undefined,
    lastInspectionDate: '',
    nextInspectionDate: '',
    location: { lat: 0, lng: 0, port: '' },
    specifications: {
      lengthOverall: 0,
      beam: 0,
      depth: 0,
      draft: 0,
      engineType: '',
      speed: 0,
    },
  });

  const [risqTypeSelection, setRisqTypeSelection] = useState<string | null>(null);
  const [risqYesNo, setRisqYesNo] = useState<Record<string, boolean | null>>({
    gravitySelfUnloading: null,
    lngFuel: null,
    gantryCranes: null,
    iceClassNotation: null,
    gearless: null,
    roRoShip: null,
    timberCarrier: null,
    grainFumigated: null,
    stsOperations: null,
    heavyLiftShip: null,
    alternativeFuelMethanol: null,
    alternativeFuelAmmonia: null,
  });

  const [cargoOperation, setCargoOperation] = useState<string>('');
  const [cargoOperationComment, setCargoOperationComment] = useState<string>('');

  const handleAddVessel = () => {
    if (!formData.name.trim() || !formData.imoNumber.trim()) return;
    addVessel({
      ...formData,
      risqClassification: {
        vesselType: risqTypeSelection,
        gravitySelfUnloading: risqYesNo.gravitySelfUnloading,
        lngFuel: risqYesNo.lngFuel,
        gantryCranes: risqYesNo.gantryCranes,
        iceClassNotation: risqYesNo.iceClassNotation,
        gearless: risqYesNo.gearless,
        roRoShip: risqYesNo.roRoShip,
        timberCarrier: risqYesNo.timberCarrier,
        grainFumigated: risqYesNo.grainFumigated,
        stsOperations: risqYesNo.stsOperations,
        heavyLiftShip: risqYesNo.heavyLiftShip,
        alternativeFuelMethanol: risqYesNo.alternativeFuelMethanol,
        alternativeFuelAmmonia: risqYesNo.alternativeFuelAmmonia,
        cargoOperation,
        cargoOperationComment: cargoOperation === 'other' ? cargoOperationComment : '',
      },
    });
    setShowAddModal(false);
    setFormData({
      name: '', imoNumber: '', type: 'Bulk Carrier', flag: '',
      deadweight: 0, grossTonnage: 0, yearBuilt: new Date().getFullYear(),
      classificationSociety: '', manager: '', superintendent: '',
      status: 'active', fleetId: 'fl-001', lastRightShipScore: undefined,
      lastInspectionDate: '', nextInspectionDate: '',
      location: { lat: 0, lng: 0, port: '' },
      specifications: { lengthOverall: 0, beam: 0, depth: 0, draft: 0, engineType: '', speed: 0 },
    });
    setRisqTypeSelection(null);
    setRisqYesNo({
      gravitySelfUnloading: null, lngFuel: null, gantryCranes: null,
      iceClassNotation: null, gearless: null, roRoShip: null,
      timberCarrier: null, grainFumigated: null, stsOperations: null,
      heavyLiftShip: null, alternativeFuelMethanol: null, alternativeFuelAmmonia: null,
    });
    setCargoOperation('');
    setCargoOperationComment('');
  };

  const filteredVessels = vessels.filter(v => {
    if (search && !v.name.toLowerCase().includes(search.toLowerCase()) && !v.imoNumber.toLowerCase().includes(search.toLowerCase())) return false;
    if (statusFilter !== 'all' && v.status !== statusFilter) return false;
    if (typeFilter !== 'all' && v.type !== typeFilter) return false;
    return true;
  });

  const vesselTypes = [...new Set(vessels.map(v => v.type))];
  const detailVessel = vessels.find(v => v.id === selectedVessel);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Fleet Management</h1>
          <p className="text-sm text-slate-500 mt-1">{vessels.length} vessels across {mockFleets.length} fleets</p>
        </div>
        <div className="flex items-center gap-2">
          <button className="px-4 py-2 bg-white border border-slate-200 rounded-xl text-sm font-medium text-slate-600 hover:bg-slate-50 flex items-center gap-2 transition-colors">
            <Download className="w-4 h-4" /> Export
          </button>
          <button onClick={() => setShowAddModal(true)} className="px-4 py-2 bg-sky-500 text-white rounded-xl text-sm font-medium hover:bg-sky-600 flex items-center gap-2 transition-colors shadow-md shadow-sky-500/20">
            <Plus className="w-4 h-4" /> Add Vessel
          </button>
        </div>
      </div>

      {/* Fleet Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {mockFleets.map(fleet => (
          <div key={fleet.id} className="bg-white rounded-2xl p-5 border border-slate-100 hover:shadow-lg transition-all cursor-pointer group">
            <div className="flex items-start justify-between mb-3">
              <div>
                <h3 className="font-semibold text-slate-800 group-hover:text-sky-600 transition-colors">{fleet.name}</h3>
                <p className="text-xs text-slate-400 mt-0.5">{fleet.vesselCount} vessels</p>
              </div>
              <Ship className="w-5 h-5 text-slate-300 group-hover:text-sky-500 transition-colors" />
            </div>
            <div className="grid grid-cols-3 gap-3 pt-3 border-t border-slate-100">
              <div>
                <p className="text-lg font-bold text-slate-800">{fleet.complianceRate}%</p>
                <p className="text-[10px] text-slate-400">Compliance</p>
              </div>
              <div>
                <p className="text-lg font-bold text-slate-800">{fleet.avgRightShipScore}</p>
                <p className="text-[10px] text-slate-400">Avg Score</p>
              </div>
              <div>
                <p className={`text-lg font-bold ${fleet.openFindings > 20 ? 'text-red-600' : 'text-amber-600'}`}>{fleet.openFindings}</p>
                <p className="text-[10px] text-slate-400">Findings</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Filters & View Toggle */}
      <div className="bg-white rounded-2xl border border-slate-100 p-4">
        <div className="flex flex-col md:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text" placeholder="Search by name or IMO number..."
              value={search} onChange={e => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
            />
          </div>
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
            className="px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm text-slate-600 focus:outline-none focus:ring-2 focus:ring-sky-500">
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="under_inspection">Under Inspection</option>
            <option value="dry_dock">Dry Dock</option>
            <option value="inactive">Inactive</option>
          </select>
          <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)}
            className="px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm text-slate-600 focus:outline-none focus:ring-2 focus:ring-sky-500">
            <option value="all">All Types</option>
            {vesselTypes.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
          <div className="flex bg-slate-100 rounded-xl p-1">
            <button onClick={() => setView('grid')} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${view === 'grid' ? 'bg-white shadow text-slate-800' : 'text-slate-500'}`}>
              <Grid3X3 className="w-4 h-4" />
            </button>
            <button onClick={() => setView('list')} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${view === 'list' ? 'bg-white shadow text-slate-800' : 'text-slate-500'}`}>
              <List className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Vessel Grid */}
      {view === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filteredVessels.map(vessel => (
            <div key={vessel.id}
              className={`bg-white rounded-2xl border transition-all duration-300 cursor-pointer hover:shadow-xl group ${selectedVessel === vessel.id ? 'border-sky-500 shadow-lg shadow-sky-500/10 ring-1 ring-sky-500' : 'border-slate-100 hover:border-slate-200'}`}
              onClick={() => setSelectedVessel(selectedVessel === vessel.id ? null : vessel.id)}
            >
              {/* Vessel Header */}
              <div className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-slate-800 group-hover:text-sky-600 transition-colors truncate">{vessel.name}</h3>
                    <p className="text-xs text-slate-400 mt-0.5">{vessel.imoNumber} · {vessel.flag}</p>
                  </div>
                  <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                    vessel.status === 'active' ? 'bg-emerald-50 text-emerald-700' :
                    vessel.status === 'under_inspection' ? 'bg-blue-50 text-blue-700' :
                    vessel.status === 'dry_dock' ? 'bg-amber-50 text-amber-700' : 'bg-slate-100 text-slate-500'
                  }`}>
                    {vessel.status === 'dry_dock' ? 'Dry Dock' : vessel.status === 'under_inspection' ? 'Inspecting' : vessel.status}
                  </span>
                </div>

                {/* Score & Key Stats */}
                <div className="flex items-center gap-4 mb-4">
                  <div className={`w-14 h-14 rounded-2xl flex items-center justify-center text-white font-bold text-lg ${getScoreBgColor(vessel.lastRightShipScore || 0)}`}>
                    {vessel.lastRightShipScore?.toFixed(1) || '—'}
                  </div>
                  <div className="flex-1 grid grid-cols-3 gap-2">
                    <div className="text-center p-2 bg-slate-50 rounded-xl">
                      <p className="text-lg font-bold text-slate-800">{vessel.openFindings}</p>
                      <p className="text-[10px] text-slate-400">Findings</p>
                    </div>
                    <div className="text-center p-2 bg-slate-50 rounded-xl">
                      <p className={`text-lg font-bold ${vessel.overdueActions > 0 ? 'text-red-600' : 'text-emerald-600'}`}>{vessel.overdueActions}</p>
                      <p className="text-[10px] text-slate-400">Overdue</p>
                    </div>
                    <div className="text-center p-2 bg-slate-50 rounded-xl">
                      <p className="text-lg font-bold text-slate-800">{vessel.totalInspections}</p>
                      <p className="text-[10px] text-slate-400">Total Insp.</p>
                    </div>
                  </div>
                </div>

                {/* Quick Info */}
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div className="flex items-center gap-2 text-slate-500">
                    <Ship className="w-3.5 h-3.5" /> {vessel.type}
                  </div>
                  <div className="flex items-center gap-2 text-slate-500">
                    <Weight className="w-3.5 h-3.5" /> {vessel.deadweight.toLocaleString()} DWT
                  </div>
                  <div className="flex items-center gap-2 text-slate-500">
                    <MapPin className="w-3.5 h-3.5" /> {vessel.location.port}
                  </div>
                  <div className="flex items-center gap-2 text-slate-500">
                    <CalendarDays className="w-3.5 h-3.5" /> Built {vessel.yearBuilt}
                  </div>
                </div>
              </div>

              {/* Expanded Details */}
              {selectedVessel === vessel.id && (
                <div className="border-t border-slate-100 p-5 bg-slate-50/50 space-y-4">
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <p className="text-xs text-slate-400 mb-0.5">Classification</p>
                      <p className="font-medium text-slate-700">{vessel.classificationSociety}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-400 mb-0.5">Manager</p>
                      <p className="font-medium text-slate-700">{vessel.manager}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-400 mb-0.5">Last Inspection</p>
                      <p className="font-medium text-slate-700">{formatDate(vessel.lastInspectionDate || '')}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-400 mb-0.5">Next Inspection</p>
                      <p className="font-medium text-slate-700">{formatDate(vessel.nextInspectionDate || '')}</p>
                    </div>
                  </div>

                  {/* Certificates */}
                  <div>
                    <p className="text-xs font-medium text-slate-500 mb-2">Certificates</p>
                    <div className="space-y-1.5">
                      {vessel.certificates.map(cert => (
                        <div key={cert.id} className="flex items-center justify-between text-xs bg-white rounded-lg px-3 py-2 border border-slate-100">
                          <span className="text-slate-600 truncate">{cert.name}</span>
                          <span className={`ml-2 flex-shrink-0 px-2 py-0.5 rounded-full font-medium ${
                            cert.status === 'valid' ? 'bg-emerald-50 text-emerald-700' :
                            cert.status === 'expiring_soon' ? 'bg-amber-50 text-amber-700' : 'bg-red-50 text-red-700'
                          }`}>
                            {cert.status === 'expiring_soon' ? 'Expiring' : cert.status === 'valid' ? 'Valid' : 'Expired'}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <button onClick={(e) => { e.stopPropagation(); navigate(`/app/fleet/${vessel.id}`); }} className="flex-1 px-4 py-2.5 bg-sky-500 text-white rounded-xl text-sm font-medium hover:bg-sky-600 transition-colors flex items-center justify-center gap-2">
                      <Eye className="w-4 h-4" /> View Details
                    </button>
                    <button onClick={(e) => { e.stopPropagation(); navigate(`/app/inspections/conduct?vessel=${vessel.id}`); }} className="flex-1 px-4 py-2.5 bg-white border border-slate-200 text-slate-700 rounded-xl text-sm font-medium hover:bg-slate-50 transition-colors flex items-center justify-center gap-2">
                      <ClipboardCheck className="w-4 h-4" /> New Inspection
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        /* List View */
        <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-100 bg-slate-50/50">
                <th className="text-left px-5 py-3 font-medium text-slate-500 text-xs">Vessel</th>
                <th className="text-left px-5 py-3 font-medium text-slate-500 text-xs hidden md:table-cell">Type / Flag</th>
                <th className="text-left px-5 py-3 font-medium text-slate-500 text-xs hidden lg:table-cell">Status</th>
                <th className="text-center px-5 py-3 font-medium text-slate-500 text-xs">Score</th>
                <th className="text-center px-5 py-3 font-medium text-slate-500 text-xs hidden md:table-cell">Findings</th>
                <th className="text-center px-5 py-3 font-medium text-slate-500 text-xs hidden lg:table-cell">CAPAs</th>
                <th className="text-left px-5 py-3 font-medium text-slate-500 text-xs hidden xl:table-cell">Location</th>
                <th className="text-left px-5 py-3 font-medium text-slate-500 text-xs hidden xl:table-cell">Last Inspection</th>
                <th className="w-10"></th>
              </tr>
            </thead>
            <tbody>
              {filteredVessels.map(v => (
                <tr key={v.id} className="border-b border-slate-50 hover:bg-slate-50/50 cursor-pointer transition-colors" onClick={() => navigate(`/app/fleet/${v.id}`)}>
                  <td className="px-5 py-3.5">
                    <p className="font-medium text-slate-800">{v.name}</p>
                    <p className="text-xs text-slate-400">{v.imoNumber}</p>
                  </td>
                  <td className="px-5 py-3.5 hidden md:table-cell">
                    <p className="text-slate-600">{v.type}</p>
                    <p className="text-xs text-slate-400">{v.flag}</p>
                  </td>
                  <td className="px-5 py-3.5 hidden lg:table-cell">
                    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                      v.status === 'active' ? 'bg-emerald-50 text-emerald-700' :
                      v.status === 'under_inspection' ? 'bg-blue-50 text-blue-700' :
                      v.status === 'dry_dock' ? 'bg-amber-50 text-amber-700' : 'bg-slate-100 text-slate-500'
                    }`}>
                      {v.status === 'dry_dock' ? 'Dry Dock' : v.status === 'under_inspection' ? 'Inspecting' : v.status}
                    </span>
                  </td>
                  <td className="px-5 py-3.5 text-center">
                    <span className={`inline-flex items-center justify-center w-10 h-10 rounded-xl text-sm font-bold text-white ${getScoreBgColor(v.lastRightShipScore || 0)}`}>
                      {v.lastRightShipScore?.toFixed(1) || '—'}
                    </span>
                  </td>
                  <td className="px-5 py-3.5 text-center hidden md:table-cell">
                    <span className={`font-semibold ${v.openFindings > 5 ? 'text-red-600' : v.openFindings > 0 ? 'text-amber-600' : 'text-emerald-600'}`}>
                      {v.openFindings}
                    </span>
                  </td>
                  <td className="px-5 py-3.5 text-center hidden lg:table-cell">
                    <span className={`font-semibold ${v.overdueActions > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                      {v.overdueActions}
                    </span>
                  </td>
                  <td className="px-5 py-3.5 hidden xl:table-cell">
                    <div className="flex items-center gap-1.5 text-slate-500 text-xs">
                      <MapPin className="w-3.5 h-3.5" /> {v.location.port}
                    </div>
                  </td>
                  <td className="px-5 py-3.5 hidden xl:table-cell">
                    <span className="text-xs text-slate-500">{formatDate(v.lastInspectionDate || '')}</span>
                  </td>
                  <td className="px-2">
                    <button className="p-1.5 rounded-lg hover:bg-slate-100"><ChevronRight className="w-4 h-4 text-slate-400" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add Vessel Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={() => setShowAddModal(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[95vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-6 border-b border-slate-100">
              <div>
                <h2 className="text-lg font-bold text-slate-900">Add New Vessel</h2>
                <p className="text-sm text-slate-500 mt-0.5">Enter vessel details to add to your fleet</p>
              </div>
              <button onClick={() => setShowAddModal(false)} className="p-2 hover:bg-slate-100 rounded-xl transition-colors">
                <X className="w-5 h-5 text-slate-400" />
              </button>
            </div>
            <div className="p-6 space-y-5">
              {/* Row 1: Name & IMO */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Vessel Name *</label>
                  <input type="text" value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g. Pacific Star" className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-sky-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">IMO Number *</label>
                  <input type="text" value={formData.imoNumber} onChange={e => setFormData({ ...formData, imoNumber: e.target.value })}
                    placeholder="e.g. IMO 9876543" className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-sky-500" />
                </div>
              </div>

              {/* Row 2: Type, Flag, Status */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Vessel Type</label>
                  <select value={formData.type} onChange={e => setFormData({ ...formData, type: e.target.value as VesselType })}
                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm text-slate-600 focus:outline-none focus:ring-2 focus:ring-sky-500">
                    <option>Bulk Carrier</option><option>Tanker</option><option>Container</option>
                    <option>General Cargo</option><option>Ro-Ro</option><option>Passenger</option><option>Offshore</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Flag State</label>
                  <input type="text" value={formData.flag} onChange={e => setFormData({ ...formData, flag: e.target.value })}
                    placeholder="e.g. Panama" className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-sky-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Status</label>
                  <select value={formData.status} onChange={e => setFormData({ ...formData, status: e.target.value as VesselStatus })}
                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm text-slate-600 focus:outline-none focus:ring-2 focus:ring-sky-500">
                    <option value="active">Active</option><option value="under_inspection">Under Inspection</option>
                    <option value="dry_dock">Dry Dock</option><option value="inactive">Inactive</option>
                  </select>
                </div>
              </div>

              {/* Row 3: DWT, GT, Year Built */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Deadweight (DWT)</label>
                  <input type="number" value={formData.deadweight || ''} onChange={e => setFormData({ ...formData, deadweight: Number(e.target.value) })}
                    placeholder="e.g. 75000" className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-sky-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Gross Tonnage</label>
                  <input type="number" value={formData.grossTonnage || ''} onChange={e => setFormData({ ...formData, grossTonnage: Number(e.target.value) })}
                    placeholder="e.g. 40000" className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-sky-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Year Built</label>
                  <input type="number" value={formData.yearBuilt} onChange={e => setFormData({ ...formData, yearBuilt: Number(e.target.value) })}
                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-sky-500" />
                </div>
              </div>

              {/* Row 4: Classification, Manager, Superintendent */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Classification Society</label>
                  <input type="text" value={formData.classificationSociety} onChange={e => setFormData({ ...formData, classificationSociety: e.target.value })}
                    placeholder="e.g. Lloyd's Register" className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-sky-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Manager</label>
                  <input type="text" value={formData.manager} onChange={e => setFormData({ ...formData, manager: e.target.value })}
                    placeholder="e.g. OceanShip Management" className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-sky-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Superintendent</label>
                  <input type="text" value={formData.superintendent} onChange={e => setFormData({ ...formData, superintendent: e.target.value })}
                    placeholder="e.g. John Smith" className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-sky-500" />
                </div>
              </div>

              {/* Row 5: Location */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Current Port / Location</label>
                <input type="text" value={formData.location.port || ''} onChange={e => setFormData({ ...formData, location: { ...formData.location, port: e.target.value } })}
                  placeholder="e.g. Singapore" className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-sky-500" />
              </div>

              {/* Row 6: Fleet */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Fleet</label>
                <select value={formData.fleetId} onChange={e => setFormData({ ...formData, fleetId: e.target.value })}
                  className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm text-slate-600 focus:outline-none focus:ring-2 focus:ring-sky-500">
                  {mockFleets.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
                </select>
              </div>

              {/* RISQ Vessel Classification Section */}
              <div className="border-t border-slate-100 pt-5">
                <h3 className="text-sm font-bold text-slate-900 mb-4">RISQ Vessel Classification</h3>

                {/* Vessel Type Selection (Radio) */}
                <div className="mb-5">
                  <p className="text-sm font-medium text-slate-700 mb-3">Please select the vessel type:</p>
                  <div className="space-y-2.5">
                    {[
                      { id: 'bulk_solid', label: 'A bulk carrier that is carrying solid bulk cargo' },
                      { id: 'bulk_grain', label: 'A bulk carrier that is carrying grain cargo' },
                      { id: 'general_cargo', label: 'A general cargo ship that is carrying general cargo and/or container cargo' },
                      { id: 'cellular_container', label: 'A cellular container ship that is carrying container cargo' },
                    ].map(option => (
                      <label key={option.id} className="flex items-center gap-3 cursor-pointer group">
                        <input
                          type="radio"
                          name="risqVesselType"
                          value={option.id}
                          checked={risqTypeSelection === option.id}
                          onChange={() => setRisqTypeSelection(option.id)}
                          className="w-4 h-4 text-sky-500 border-slate-300 focus:ring-sky-500"
                        />
                        <span className="text-sm text-slate-600 group-hover:text-slate-900 transition-colors">{option.label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Yes/No Questions */}
                <div className="space-y-4">
                  {([
                    { key: 'gravitySelfUnloading', label: 'A gravity-based self-unloading and/or hybrid self-unloading vessel' },
                    { key: 'lngFuel', label: 'The ship is using LNG fuel' },
                    { key: 'gantryCranes', label: 'The ship is equipped with gantry cranes that can travel along the main deck' },
                    { key: 'iceClassNotation', label: 'The vessel has an ice class notation, is currently navigating or intends to navigate in icy areas or possesses a polar water certificate' },
                    { key: 'gearless', label: 'The vessel is gearless' },
                    { key: 'roRoShip', label: 'The vessel is a Ro-Ro ship' },
                    { key: 'timberCarrier', label: 'The vessel is a timber carrier' },
                    { key: 'grainFumigated', label: 'A bulk carrier that is carrying grain cargo - the cargo spaces had been fumigated at least once in the past 12 months' },
                    { key: 'stsOperations', label: 'Is the vessel involved in STS operations or planning to be involved in STS operations?' },
                    { key: 'heavyLiftShip', label: 'Is the vessel a Heavy Lift Ship?' },
                    { key: 'alternativeFuelMethanol', label: 'Is the vessel using Alternative Fuel – Methanol?' },
                    { key: 'alternativeFuelAmmonia', label: 'Is the vessel using Alternative Fuel – Ammonia?' },
                  ] as { key: string; label: string }[]).map(q => (
                    <div key={q.key}>
                      <p className="text-sm font-medium text-slate-700 mb-2">{q.label}</p>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => setRisqYesNo(prev => ({ ...prev, [q.key]: false }))}
                          className={`px-5 py-1.5 rounded-lg text-sm font-medium border transition-all ${
                            risqYesNo[q.key] === false
                              ? 'bg-sky-500 text-white border-sky-500 shadow-md shadow-sky-500/20'
                              : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                          }`}
                        >
                          No
                        </button>
                        <button
                          type="button"
                          onClick={() => setRisqYesNo(prev => ({ ...prev, [q.key]: true }))}
                          className={`px-5 py-1.5 rounded-lg text-sm font-medium border transition-all ${
                            risqYesNo[q.key] === true
                              ? 'bg-sky-500 text-white border-sky-500 shadow-md shadow-sky-500/20'
                              : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                          }`}
                        >
                          Yes
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Cargo Operation */}
                <div className="mt-5">
                  <p className="text-sm font-medium text-slate-700 mb-2">Cargo operation</p>
                  <select
                    value={cargoOperation}
                    onChange={e => setCargoOperation(e.target.value)}
                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm text-slate-600 focus:outline-none focus:ring-2 focus:ring-sky-500"
                  >
                    <option value="">Select cargo operation</option>
                    <option value="loading">Loading</option>
                    <option value="discharging">Discharging</option>
                    <option value="discharging_back_load">Discharging & Back Load</option>
                    <option value="bunkering">Bunkering</option>
                    <option value="other">Other Operation (please specify in the comments section below)</option>
                  </select>
                  {cargoOperation === 'other' && (
                    <div className="mt-3">
                      <textarea
                        value={cargoOperationComment}
                        onChange={e => setCargoOperationComment(e.target.value)}
                        placeholder="Please specify the cargo operation..."
                        rows={3}
                        className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm text-slate-600 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-sky-500 resize-none"
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 p-6 border-t border-slate-100">
              <button onClick={() => setShowAddModal(false)} className="px-5 py-2.5 bg-white border border-slate-200 text-slate-700 rounded-xl text-sm font-medium hover:bg-slate-50 transition-colors">
                Cancel
              </button>
              <button onClick={handleAddVessel} disabled={!formData.name.trim() || !formData.imoNumber.trim()}
                className="px-5 py-2.5 bg-sky-500 text-white rounded-xl text-sm font-medium hover:bg-sky-600 transition-colors shadow-md shadow-sky-500/20 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2">
                <Plus className="w-4 h-4" /> Add Vessel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
